import { useGetAsignmentFieldsQuery, useGetCurrentUserQuery } from 'services';
import { article_order_status } from 'constant/static';

import { useForeignAuthorProcess } from './useForeignAuthorProcess';
import { useUserHasPermission } from './useUserHasPermission';

/**
 * @description Medya teklifi `decider` rolleri (BE `MediaOfferDecider` enum'unun birebir aynası).
 * Değerler BE string'leriyle aynı OLMALI: satır silme yetkisi `record.createdByRole` ile bu
 * değerlerin karşılaştırılmasına dayanır, değer kayarsa eşleşme bozulur.
 */
export const MEDIA_OFFER_DECIDER = {
  author: 'author',
  ihControl: 'ih_control',
  ihControlTranslate: 'ih_control_translate',
  supervisor: 'supervisor',
  postOrderMediaOfferControl: 'post_order_media_offer_control',
  postOrderEuControl: 'post_order_eu_control',
  postOrderOwnerCommitteeControl: 'post_order_owner_committee_control',
  postOrderSupervisor: 'post_order_supervisor',
  postOrderCitationSourceRedaction: 'post_order_citation_source_redaction',
  postOrderTranslationTextRedaction: 'post_order_translation_text_redaction',
  postOrderOrthographyRedaction: 'post_order_orthography_redaction',
  postOrderProofreadingControl: 'post_order_proofreading_control',
  postOrderPubControl: 'post_order_pub_control',
};

/**
 * @description Yetki modları — matristeki tek üç seçenek, dördüncüsü yoktur.
 * - `full`    : ekle + tüm alanları düzenle + karar (uygundur/uygun değildir) + not + kendi satırını sil
 * - `limited` : yalnız Altyazı / Kaynak / Link düzenle + not. Ekleme, silme ve karar YOK.
 * - `view`    : yalnız salt-okunur detay modalı.
 */
export const MEDIA_OFFER_MODE = {
  full: 'full',
  limited: 'limited',
  view: 'view',
};

/**
 * @description Kimlik kontrolü türleri — "bu statüde yetkili kişi sen misin?" sorusunun nasıl
 * cevaplanacağını belirler. `assignment` türü, yetkinin global bir rol bayrağından DEĞİL maddeye
 * yapılan görev atamasından geldiğini söyler (redaksiyon aşamaları).
 */
const IDENTITY = {
  // Maddenin yazarı — yabancı yazarlı maddelerde yazar adına işlem yapan heyet başkanı/sekreteri dahil.
  author: 'author',
  ownerCommittee: 'ownerCommittee',
  articleSupervisor: 'articleSupervisor',
  roleFlag: 'roleFlag',
  assignment: 'assignment',
};

/** Atama sorgusunun (`assignment_fields`) süreç tipi: 0 = Ansiklopedi Birimi, 1 = Yayın Müdürlüğü. */
const ASSIGNMENT_TYPE = { encyclopediaUnit: 0, publishingUnit: 1 };

/**
 * @description **Yetki matrisi — tek doğruluk kaynağı.** `ArticleOffer.status` → o statüde kimin,
 * hangi modda işlem yapabildiği. Burada listelenmeyen her statüde yetki yoktur (yalnız salt-görüntüleme
 * + admin muafiyeti).
 *
 * Matrisin dayanağı ürün izahındaki Rol/Durum tablosu ve WRK-38950 / 38966 / 39388 talepleridir:
 * - Yayın Müdürlüğü'nün DÖRT redaksiyon aşaması (40 Atıf-Kaynaklar, 41 Çeviri-Yazı, 42 İmla,
 *   52 Editöryal) `limited` moddadır: altyazı/kaynak/link düzeltir, not yazar, **karar veremez**.
 * - Karar aşamaları (24, 49, 36, 37, 38, 53, 43) `full` moddadır.
 * - Redaksiyon aşamalarında kimlik ATAMA ile belirlenir; global rol bayrağı yeterli değildir.
 * - Yabancı yazar sürecinde yazar aşamasının (22) kimliği heyet başkan/sekreterini de kapsar (matris
 *   değişmez, kimlik kontrolü genişler); tercüme aşaması (54) `ih_control_translate` biriminindir. Sürecin tamamı
 *   için bkz. `docs/foreign-author-process.md`.
 * - `decisionRequired`: aşamada her medya teklifine karar verilmesi **zorunludur** — "Karar bekliyor"
 *   işareti ve aşama onayı kilidi yalnız bu aşamalarda uygulanır. Tek zorunlu aşama 36 (AB - Medya
 *   Teklifi Kontrolü); BE de kilidi yalnız burada zorlar. Diğer karar aşamalarında karar opsiyoneldir:
 *   önceki kararlar geçerli kalır, yetkili isterse günceller. Verilmezse `false`.
 *
 * @type {Record<number, { decider: string, mode: string, decisionRequired?: boolean, identity: { kind: string, roleFlag?: string, type?: number } }>}
 */
const STATUS_RULE = {
  // ——— Sipariş ve Telif ———
  [article_order_status['Yazar-Gündemde']]: {
    decider: MEDIA_OFFER_DECIDER.author,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.author },
  },
  [article_order_status['IH-Madde Kontrol']]: {
    decider: MEDIA_OFFER_DECIDER.ihControl,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.ownerCommittee },
  },
  // Tercüme aşamasında medyayı yine sahibi heyet yönetir ama BE bu aşamaya AYRI bir decider yazar
  // (`ih_control_translate`); 24'ün `ih_control`'ü kullanılırsa silme sahipliği ve karar eşleşmesi kırılır.
  [article_order_status['IH - Tercüme']]: {
    decider: MEDIA_OFFER_DECIDER.ihControlTranslate,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.ownerCommittee },
  },
  [article_order_status['IH - Madde Sorumlusu Kontrol']]: {
    decider: MEDIA_OFFER_DECIDER.supervisor,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.articleSupervisor },
  },

  // ——— Telif Sonrası · Ansiklopedi Birimi ———
  [article_order_status['AB - Medya Teklifi Kontrolü']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderMediaOfferControl,
    mode: MEDIA_OFFER_MODE.full,
    decisionRequired: true,
    identity: { kind: IDENTITY.assignment, type: ASSIGNMENT_TYPE.encyclopediaUnit },
  },
  [article_order_status['Ansiklopedi Birimi Kontrol']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderEuControl,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.roleFlag, roleFlag: 'euManager' },
  },
  [article_order_status['TS - İH Madde Kontrol']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderOwnerCommitteeControl,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.ownerCommittee },
  },
  [article_order_status['TS - Madde Sorumlusu Kontrol']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderSupervisor,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.articleSupervisor },
  },

  // ——— Telif Sonrası · Yayın Müdürlüğü ———
  [article_order_status['YM - Atıf-Kaynaklar Redaksiyonu']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderCitationSourceRedaction,
    mode: MEDIA_OFFER_MODE.limited,
    identity: { kind: IDENTITY.assignment, type: ASSIGNMENT_TYPE.publishingUnit },
  },
  [article_order_status['YM - Çeviri-Yazı Redaksiyonu']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderTranslationTextRedaction,
    mode: MEDIA_OFFER_MODE.limited,
    identity: { kind: IDENTITY.assignment, type: ASSIGNMENT_TYPE.publishingUnit },
  },
  [article_order_status['Yayin Mudurlugu Imla Redaksiyon']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderOrthographyRedaction,
    mode: MEDIA_OFFER_MODE.limited,
    identity: { kind: IDENTITY.assignment, type: ASSIGNMENT_TYPE.publishingUnit },
  },
  [article_order_status['Yayin Mudurlugu Editoryal Son Okuma']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderProofreadingControl,
    mode: MEDIA_OFFER_MODE.limited,
    identity: { kind: IDENTITY.assignment, type: ASSIGNMENT_TYPE.publishingUnit },
  },
  [article_order_status['Yayin Mudurlugu Genel Kontrol']]: {
    decider: MEDIA_OFFER_DECIDER.postOrderPubControl,
    mode: MEDIA_OFFER_MODE.full,
    identity: { kind: IDENTITY.roleFlag, roleFlag: 'pubManager' },
  },
};

/**
 * @description **Medya Teklifi Kontrolü rol penceresi.** Bu rolün işi maddenin tek bir adımı değil
 * medyanın kendisidir: Ansiklopedi Birimi fazının tamamında (atama + tüm AB redaksiyon adımları +
 * kendi aşaması + AB Kontrol) medya üzerinde çalışabilir. Madde Yayın Müdürlüğü'ne geçtiğinde
 * (statü 38 ve sonrası) pencere kapanır — orada aşamanın kendi sahibi vardır.
 *
 * Pencere yalnız DÜZENLEME yetkisi verir; karar (`canDecide`) aşamaya bağlıdır, aşaması olmayan
 * statülerde kimseye karar açılmaz.
 */
const MEDIA_CONTROL_WINDOW = [
  article_order_status['Telif Sonrasi'], // 27
  article_order_status['AB - Madde Başlığı Redaksiyonu'], // 28
  article_order_status['AB - Ayet Hadis Kontrolü'], // 30
  article_order_status['AB - Madde Özü Yazımı ve Redaksiyonu'], // 31
  article_order_status['AB - Kimlik Kartı Redaksiyonu'], // 32
  article_order_status['AB - Üst Veri ve Kavram Ağacı Redaksiyonu'], // 33
  article_order_status['AB - Üslup/Metin Redaksiyonu'], // 34
  article_order_status['AB - Metin İçi Veri ve Atıf-İlişkili Madde Kontrolü'], // 35
  article_order_status['AB - Yazma Eser Kontrolü'], // 51
  article_order_status['AB - Medya Teklifi Kontrolü'], // 36
  article_order_status['Ansiklopedi Birimi Kontrol'], // 37
];

/**
 * @description Telif sonrası (post-order) birimleri — `MEDIA_OFFER_DECIDER`'ın `post_order_*` alt kümesi.
 * Telif öncesi (`author` / `ih_control` / `supervisor`) bu kümeye dahil değildir.
 */
const AFTER_COPYRIGHT_DECIDERS = new Set([
  MEDIA_OFFER_DECIDER.postOrderMediaOfferControl,
  MEDIA_OFFER_DECIDER.postOrderEuControl,
  MEDIA_OFFER_DECIDER.postOrderOwnerCommitteeControl,
  MEDIA_OFFER_DECIDER.postOrderSupervisor,
  MEDIA_OFFER_DECIDER.postOrderCitationSourceRedaction,
  MEDIA_OFFER_DECIDER.postOrderTranslationTextRedaction,
  MEDIA_OFFER_DECIDER.postOrderOrthographyRedaction,
  MEDIA_OFFER_DECIDER.postOrderProofreadingControl,
  MEDIA_OFFER_DECIDER.postOrderPubControl,
]);

/**
 * @description Verilen decider telif sonrası bir birime mi ait? "Yalnız telif sonrasında görünür"
 * UI parçalarını (değerlendirme timeline'ı) kapılamak için kullanılır.
 * @param {string|null|undefined} decider `MEDIA_OFFER_DECIDER` değerlerinden biri.
 * @returns {boolean}
 */
export const isAfterCopyrightDecider = (decider) => AFTER_COPYRIGHT_DECIDERS.has(decider);

/**
 * @description Yetkisiz (salt-görüntüleme) varsayılan yetki nesnesi. `offer`/`current_user` henüz
 * yokken ve yetki hiç geçilmeyen bağlamlarda kullanılır — yetki varsayılanı her zaman KAPALI'dır.
 * @type {MediaOfferPermission}
 */
export const MEDIA_OFFER_NO_PERMISSION = Object.freeze({
  activeDecider: null,
  decisionRequired: false,
  actingAs: null,
  mode: MEDIA_OFFER_MODE.view,
  canManage: false,
  isReadOnly: true,
  canManageAllRows: false,
  limitedEdit: false,
  canDecide: false,
  isAuthorActor: false,
});

/**
 * @typedef {Object} MediaOfferPermission
 * @property {string|null} activeDecider Maddenin statüsüne karşılık gelen aşamanın birimi
 *   (`MEDIA_OFFER_DECIDER`); aşamasız statülerde `null`. **Statünün özelliğidir, kullanıcının değil** —
 *   karar kartının başlığı buradan beslenir.
 * @property {boolean} decisionRequired Aktif aşama medya tekliflerine karar vermek ZORUNDA mı. Statünün
 *   özelliğidir, `STATUS_RULE`'daki `decisionRequired`'dan okunur (yalnız 36'da `true`). "Karar bekliyor"
 *   işareti ve aşama onayı kilidi buna bakar.
 * @property {string|null} actingAs Current_user'ın bu maddede hangi birim adına işlem yaptığı.
 *   Satır silme yetkisi (`createdByRole` eşleşmesi) buna bakar. Yetkisi yoksa `null`.
 * @property {string} mode `MEDIA_OFFER_MODE` — current_user'ın bu maddedeki etkin modu.
 * @property {boolean} canManage Düzenleme (revize) yetkisi var mı (`mode !== 'view'`).
 * @property {boolean} isReadOnly `!canManage`.
 * @property {boolean} canManageAllRows Satır sahipliği kurallarından muafiyet — yalnız admin.
 * @property {boolean} limitedEdit Yalnız Altyazı / Kaynak / Link düzenlenebilir (`mode === 'limited'`).
 *   Ekleme ve silme kapalı, karar verilemez; değerlendirme notu yazılabilir.
 * @property {boolean} canDecide Uygundur / Uygun Değildir kararı verilebilir mi.
 * @property {boolean} isAuthorActor Current_user bu maddede YAZAR olarak işlem yapıyor mu. Yazar,
 *   heyetin/birimlerin eklediği satırlara müdahale edemez (yalnız kendi eklediklerini düzenler).
 */

/**
 * @description **Medya teklifi yetkisinin tek karar noktası.** Maddenin statüsünü `STATUS_RULE`
 * matrisinden geçirir, current_user'ın o statüdeki kimliğini doğrular ve tüm UI'ın kullanacağı yetki
 * bayraklarını döner. Gerçek yetkiyi her zaman BE zorlar; bu katman görünürlük içindir.
 *
 * Tasarım kuralları (ekranlar bunlara müdahale etmez):
 * 1. **Yetki ekrandan bağımsızdır.** Aynı kullanıcı aynı maddeyi hangi ekranda açarsa açsın aynı
 *    aksiyonları görür — her ekran bu hook'un dönüşünü olduğu gibi geçirir.
 * 2. **Yetki, aşama çubuğunda seçili adıma değil `offer.status`'e bakar.** Atama kimliği de statüye
 *    karşılık gelen adımın atamasından okunur; kullanıcı geçmiş bir adımı görüntülemek için
 *    tıkladığında yetkisi değişmez.
 * 3. **Satır sahipliği düzenlemeyi kısıtlamaz** (yalnız yazar istisnası + silme kuralı) — yetkili
 *    kullanıcı tablodaki her satırı düzenler. Düzenleme yıkıcı değildir: `/revise` yeni versiyon
 *    üretir, önceki birimin orijinali versiyon zincirinde ve işlem geçmişinde durur.
 *
 * Saf/yan etkisizdir; `offer` ya da current_user yokken güvenli şekilde salt-görüntüleme döner.
 * Atama sorgusu yalnız kimliği atamaya bağlı statülerde atılır (diğerlerinde `skip`).
 *
 * @param {Object|null} offer Madde teklifi (`ArticleOffer`): `id`, `status`, `authors`,
 *   `ownerCommittee`, `articleSupervisor`.
 * @returns {MediaOfferPermission} Yetki bayrakları.
 */
export const useMediaOfferPermission = (offer) => {
  const { data: current_user } = useGetCurrentUserQuery();
  const { handlePermission, isArticleSupervisor, authorHasPermission } = useUserHasPermission();
  // Yabancı yazarlı maddede yazarın işlerini heyet yürütür; kural tek kaynaktan okunur, burada
  // yeniden hesaplanmaz. Erken return'ün üstünde çağrılmalı (React hook sırası).
  const { canActAsAuthor } = useForeignAuthorProcess(offer);

  const rule = offer ? STATUS_RULE[offer.status] : undefined;

  // Kimlik ataması gereken statülerde (redaksiyon aşamaları) görev atamalarını hook'un KENDİSİ çeker:
  // yetki hesabı böylece çağıran ekranın state'ine (seçili adım vb.) bağlı kalmaz. Diğer statülerde
  // sorgu hiç atılmaz. Tablo zaten aynı sorguyu kullandığı için RTK Query ek istek üretmez.
  const needsAssignment = rule?.identity.kind === IDENTITY.assignment;
  const { data: assignments } = useGetAsignmentFieldsQuery(
    { type: rule?.identity.type, articleOffer: offer?.id },
    { skip: !needsAssignment || !offer?.id }
  );

  if (!offer || !current_user) return MEDIA_OFFER_NO_PERMISSION;

  const activeDecider = rule?.decider ?? null;
  const decisionRequired = Boolean(rule?.decisionRequired);
  const isAdmin = Boolean(current_user.isAdmin);

  // Aşamanın sahibi biz miyiz? Kimlik kontrolü matristeki `identity` türüne göre yapılır.
  let isStageActor = false;
  switch (rule?.identity.kind) {
    case IDENTITY.author:
      // Yabancı yazarlı maddede yazara sistemden bilgilendirme gitmediği için madde ve medyayı
      // sorumlu heyetin başkanı/sekreteri yükler; BE bu satırları `author` olarak işaretlediği için
      // ayrı bir decider açılmaz, heyet doğrudan yazar dalının içine alınır.
      isStageActor = Boolean(authorHasPermission(offer.authors ?? [])) || canActAsAuthor;
      break;
    case IDENTITY.ownerCommittee:
      // Maddenin sahibi heyetin yöneticisi → komitede başkan (type 0) ya da sekreter (type 1).
      isStageActor = handlePermission(offer.ownerCommittee?.id);
      break;
    case IDENTITY.articleSupervisor:
      isStageActor = isArticleSupervisor(offer.articleSupervisor?.id);
      break;
    case IDENTITY.roleFlag:
      isStageActor = Boolean(current_user[rule.identity.roleFlag]);
      break;
    case IDENTITY.assignment:
      // Kimlik, maddenin GÜNCEL statüsüne karşılık gelen adımın atamasından okunur — ekranda
      // seçili adımdan değil (bkz. tasarım kuralı 2).
      isStageActor = Boolean(
        assignments
          ?.find((step) => step.status === offer.status)
          ?.assignments?.some((assignment) => assignment.user?.id === current_user.id)
      );
      break;
    default:
      isStageActor = false;
  }

  // Medya Teklifi Kontrolü rol penceresi: AB fazının tamamında medyanın sahibi bu roldür.
  const isMediaControlActor =
    Boolean(current_user.isPostOrderMediaOfferControl) && MEDIA_CONTROL_WINDOW.includes(offer.status);

  // Etkin mod: admin her yerde tam yetkilidir; aşamanın sahibi aşamanın modunu alır; rol penceresi
  // tam düzenleme verir; kalan herkes salt-görüntülemedir.
  let mode = MEDIA_OFFER_MODE.view;
  if (isAdmin) mode = MEDIA_OFFER_MODE.full;
  else if (isStageActor) mode = rule.mode;
  else if (isMediaControlActor) mode = MEDIA_OFFER_MODE.full;

  // Hangi birim adına işlem yapıyoruz — satır silme yetkisi (`createdByRole` eşleşmesi) buna bakar.
  let actingAs = null;
  if (isStageActor) actingAs = activeDecider;
  else if (isMediaControlActor) actingAs = MEDIA_OFFER_DECIDER.postOrderMediaOfferControl;
  else if (isAdmin) actingAs = activeDecider;

  const canManage = mode !== MEDIA_OFFER_MODE.view;
  const limitedEdit = mode === MEDIA_OFFER_MODE.limited;

  return {
    activeDecider,
    decisionRequired,
    actingAs,
    mode,
    canManage,
    isReadOnly: !canManage,
    // Admin, satır sahipliği kurallarından muaftır: her satırı düzenler ve siler.
    canManageAllRows: isAdmin,
    limitedEdit,
    // Karar bir AŞAMAYA aittir: aktif aşama yoksa kime yazılacağı tanımsızdır → karar kartı açılmaz.
    // Sınırlı moddaki redaksiyon birimleri karar veremez, yalnız değerlendirme notu yazar (WRK-39388).
    canDecide: canManage && Boolean(activeDecider) && !limitedEdit,
    // Yazar heyetin/birimlerin eklediği satırlara müdahale edemez — admin bu kısıttan muaftır.
    isAuthorActor: !isAdmin && actingAs === MEDIA_OFFER_DECIDER.author,
  };
};
